<script lang="ts">
	import classNames from 'classnames';
	import { onMount, type Component } from 'svelte';
	import { twMerge } from 'tailwind-merge';
	import { EditionsSection } from './components/EditionsSection';
	import { ExhibitionsSection } from './components/ExhibitionsSection';
	import { MerchSection } from './components/MerchSection';
	import { NftsSection } from './components/NftsSection';
	import { PodcastsSection } from './components/PodcastsSection';
	import { PrimarySection } from './components/PrimarySection';
	import { PublishingSection } from './components/PublishingSection';
	import { TalksSection } from './components/TalksSection';
	import {
		getArtistSections,
		type ArtistSectionKey,
	} from './utils/getArtistSections';
	import { getSectionId as buildSectionId } from './utils/getSectionId';
	import { HorizontalScrollControls } from '#lib/components/HorizontalScrollControls/index.js';
	import { trackOnwardClicks } from '#lib/features/analytics/utils/trackOnwardClicks/trackOnwardClicks.js';
	import {
		horizontalScrollHint,
		initialHorizontalScrollState,
		type HorizontalScrollState,
	} from '#lib/utils/horizontalScrollHint/index.js';
	import { scrollTabIntoView } from '#lib/utils/scrollTabIntoView/scrollTabIntoView.js';
	import { ArtistsMixpanelEvents } from '#lib/websites/artists/constants/mixpanel-events.js';
	import { page } from '$app/state';
	import { Txt } from '$global/components/Txt';
	import { replaceShallowState } from '$global/utils/replaceShallowState/replaceShallowState';
	import type { ArtistsPageData } from '$routes/artists/[slug]/types';

	interface Props {
		dataCy: string;
		class?: string;
	}

	let { ...props }: Props = $props();

	let dataCyPrefix = $derived(`${props.dataCy}-projects`);
	let artist = $derived(page.data.artist as ArtistsPageData['artist']);

	// The artist page → collection → artwork journey (HB-967). Every rail below
	// (Editions, Primary, Merch, Books, NFTs, Talks, Podcasts, Exhibitions) links
	// into a different section, so one delegated listener over all of them covers
	// the whole outbound half of that journey.
	//
	// The tab bar's `#section` links resolve to no section and are dropped, so
	// jumping between rails is not mistaken for leaving the page.
	let onwardClickParams = $derived({
		eventName: ArtistsMixpanelEvents.OnwardClick,
		sourceElement: 'artist-projects',
		fromSection: 'artists' as const,
		artist: artist?.name ?? undefined,
	});
	let catalogue = $derived(page.data.catalogue as ArtistsPageData['catalogue']);
	let booksResponse = $derived(
		page.data.booksResponse as ArtistsPageData['booksResponse']
	);
	let sections = $derived(
		getArtistSections({ artist, catalogue, booksResponse })
	);

	const SECTION_COMPONENTS: Record<
		ArtistSectionKey,
		Component<{ dataCy: string }>
	> = {
		editions: EditionsSection,
		primary: PrimarySection,
		merch: MerchSection,
		books: PublishingSection,
		nfts: NftsSection,
		talks: TalksSection,
		podcasts: PodcastsSection,
		exhibitions: ExhibitionsSection,
	};

	const getSectionId = (key: ArtistSectionKey) =>
		buildSectionId(dataCyPrefix, key);

	let activeSection: ArtistSectionKey | '' = $state('');
	let sectionNavigation: HTMLDivElement | undefined = $state();
	let sectionNavigationState: HorizontalScrollState = $state(
		initialHorizontalScrollState
	);
	let navigationTarget: ArtistSectionKey | '' = $state('');
	let navigationTimeout: number | undefined;
	let navigationFrame: number | undefined;

	$effect(() => {
		const availableKeys = sections.map(({ key }) => key);
		if (!activeSection || !availableKeys.includes(activeSection)) {
			activeSection = availableKeys[0] || '';
		}
	});

	// Keep the active item visible in the horizontally scrollable mobile index.
	$effect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		activeSection;
		if (!sectionNavigation || !activeSection) return;

		requestAnimationFrame(() => {
			scrollTabIntoView(
				sectionNavigation,
				sectionNavigation?.querySelector<HTMLElement>(
					`[data-section-key="${activeSection}"]`
				)
			);
		});
	});

	onMount(() => {
		let frame: number | undefined;

		const updateActiveSection = () => {
			frame = undefined;
			const activationLine =
				sectionNavigation?.getBoundingClientRect().bottom ?? 96;

			// Keep the clicked tab active while its smooth scroll is running rather
			// than briefly highlighting every intervening section.
			if (navigationTarget) {
				activeSection = navigationTarget;
				return;
			}

			let currentSection = sections[0]?.key || '';
			const positionedSections = sections.flatMap(({ key }) => {
				const element = document.getElementById(getSectionId(key));
				return element
					? [{ key, top: element.getBoundingClientRect().top }]
					: [];
			});

			// Layout-less environments can report every section at exactly the
			// same position. Retain the first section instead of selecting the last.
			if (
				positionedSections.length > 1 &&
				positionedSections.every(
					({ top }) => top === positionedSections[0]?.top
				)
			) {
				return;
			}

			// The section just scrolled to rests at its scroll-mt offset, which can
			// leave its top a few px below the sticky bar's bottom; a strict
			// `top <= activationLine` then skips it and the section above wins. So
			// after collecting every section at/above the line, also adopt the very
			// next one when it is resting within a small band just below the line
			// (i.e. it is effectively the section in view).
			const RESTING_BAND = 8;

			for (const { key, top } of positionedSections) {
				if (top <= activationLine + RESTING_BAND) {
					currentSection = key;
				} else {
					break;
				}
			}

			activeSection = currentSection;
		};

		const scheduleUpdate = () => {
			if (frame !== undefined) return;
			frame = requestAnimationFrame(updateActiveSection);
		};

		updateActiveSection();
		window.addEventListener('scroll', scheduleUpdate, { passive: true });
		window.addEventListener('resize', scheduleUpdate);

		// Preserve old shareable ?section= links after removing the tab model.
		const legacySection = page.url.searchParams.get('section');
		const legacyMatch = sections.find(({ key }) => key === legacySection);
		if (legacyMatch) {
			activeSection = legacyMatch.key;
			requestAnimationFrame(() => {
				document
					.getElementById(getSectionId(legacyMatch.key))
					?.scrollIntoView({ block: 'start' });
			});
		}

		return () => {
			if (frame !== undefined) cancelAnimationFrame(frame);
			if (navigationTimeout !== undefined) {
				window.clearTimeout(navigationTimeout);
			}
			if (navigationFrame !== undefined) {
				cancelAnimationFrame(navigationFrame);
			}
			window.removeEventListener('scroll', scheduleUpdate);
			window.removeEventListener('resize', scheduleUpdate);
		};
	});

	const navigateToSection = (
		event: MouseEvent,
		sectionKey: ArtistSectionKey
	) => {
		event.preventDefault();

		const element = document.getElementById(getSectionId(sectionKey));
		if (!element) return;

		activeSection = sectionKey;
		navigationTarget = sectionKey;
		// SvelteKit's shallow routing rather than history.replaceState: the raw call
		// nulls the entry's state, wiping SvelteKit's history bookkeeping and the
		// persisted "Show more" section counts along with it.
		replaceShallowState(`#${getSectionId(sectionKey)}`, page.state);
		element.scrollIntoView({ behavior: 'smooth', block: 'start' });

		if (navigationTimeout !== undefined) {
			window.clearTimeout(navigationTimeout);
		}
		if (navigationFrame !== undefined) {
			cancelAnimationFrame(navigationFrame);
		}

		const releaseNavigationLock = () => {
			navigationTarget = '';
			navigationFrame = undefined;
			if (navigationTimeout !== undefined) {
				window.clearTimeout(navigationTimeout);
				navigationTimeout = undefined;
			}
		};

		// Hold the clicked tab active until the smooth scroll SETTLES on the target
		// — not a fixed timer (which a long scroll outlasts, flashing intermediate
		// sections) and not merely "top crossed the line" (true from frame one for
		// an upward scroll, which flashes sections on the way up). Instead wait for
		// the target's top to stop moving near the activation line, which holds
		// correctly in both directions.
		const activationLine =
			sectionNavigation?.getBoundingClientRect().bottom ?? 96;
		const RESTING_BAND = 8;
		let previousTop = Number.NaN;

		const waitForArrival = () => {
			const top = element.getBoundingClientRect().top;
			const nearLine = Math.abs(top - activationLine) <= RESTING_BAND;
			const stopped = Math.abs(top - previousTop) < 1;

			if (nearLine && stopped) {
				releaseNavigationLock();
				return;
			}

			previousTop = top;
			navigationFrame = requestAnimationFrame(waitForArrival);
		};
		navigationFrame = requestAnimationFrame(waitForArrival);

		// Fallback: never hold the lock indefinitely if the scroll is interrupted
		// (e.g. the user scrolls away) and the target never reaches the top.
		navigationTimeout = window.setTimeout(releaseNavigationLock, 1500);
	};
</script>

<div
	class={twMerge('flex flex-col', props.class)}
	use:trackOnwardClicks={onwardClickParams}
>
	<!--
		Pins under the global bar, and under the platform sub-nav too once that
		stops being hidden at `lg` (47px + the sub-nav's 56px).
	-->
	<nav
		aria-label="Artist project sections"
		class="sticky top-[47px] z-[11] bg-white lg:top-[103px]"
		data-cy={`${dataCyPrefix}-tabs`}
	>
		<div class="relative">
			<div
				bind:this={sectionNavigation}
				use:horizontalScrollHint={{
					onChange: (state) => (sectionNavigationState = state),
				}}
				class="horizontal-scroll-hint no-scrollbar flex w-full overflow-x-auto border-t border-gray-200"
			>
				{#each sections as section (section.key)}
					<div class="relative -mb-px shrink-0">
						<a
							href={`#${getSectionId(section.key)}`}
							aria-current={activeSection === section.key
								? 'location'
								: undefined}
							class={classNames(
								'block h-full w-full px-3 py-3',
								activeSection === section.key ? '' : 'text-gray-500'
							)}
							data-cy={`${dataCyPrefix}-tab-${section.key}`}
							data-section-key={section.key}
							onclick={(event) => navigateToSection(event, section.key)}
						>
							<Txt variant="body2" class="tracking-[.14em] uppercase">
								{section.label}
							</Txt>
						</a>
						<hr
							class={classNames(
								'absolute bottom-[1px] z-[10] w-full',
								activeSection === section.key
									? 'border-gray-900'
									: 'border-gray-200'
							)}
						/>
					</div>
				{/each}
			</div>
			<hr class="mt-[-1px]" />

			<!--
				The chevrons line up with the nav bar's logo and hamburger above: both
				bars share this Container's content box, and those icons sit flush at
				its edges — hence no outer padding.

				Inwards, the tab under the chevron must be dimmed without being erased.
				Explicit stop positions decouple "stays solid" from "fades": white holds
				only to 30% (just clearing the chevron), then ramps through a partial
				midpoint to transparent — visible but not a solid slab. A plain
				from/via/to ramp can't express this, since its midpoint is pinned to
				50%. `via-white/60` rather than `via-transparent` — Tailwind compiles
				the latter to rgba(0,0,0,0), so the ramp would pass through zero-alpha
				black and grey the tabs as it faded.

				`min-w-0` clears the shared `min-w-11` floor, which would otherwise
				keep the button ~44px wide no matter how small the padding gets — the
				chevron then sits tight against the edge while pr-12/pl-12 gives the
				fade a run to travel over.

				`pointer-events-none` makes that fade run click-through: it overlays
				the adjacent tab, so a clickable button spanning its full width left
				sections like BOOKS visible but unclickable at some widths. The
				chevron re-enables pointer events on itself, so only the icon
				actually takes clicks.
			-->
			<HorizontalScrollControls
				target={sectionNavigation}
				state={sectionNavigationState}
				previousLabel="Show previous artist sections"
				nextLabel="Show more artist sections"
				class="lg:hidden"
				classes={{
					previousButton:
						'pointer-events-none min-w-0 pl-0 pr-12 from-white from-30% via-white/60 via-65% to-transparent',
					nextButton:
						'pointer-events-none min-w-0 pr-0 pl-12 from-white from-30% via-white/60 via-65% to-transparent',
				}}
			/>
		</div>
	</nav>

	{#each sections as section, index (section.key)}
		{@const SectionComponent = SECTION_COMPONENTS[section.key]}
		<!--
			Clears the pinned chrome above the section: global bar + tab bar, plus
			the platform sub-nav from `lg` (80px + 56px).
		-->
		<section
			id={getSectionId(section.key)}
			class={classNames(
				'scroll-mt-20 lg:scroll-mt-[136px]',
				index === 0 && 'first-section'
			)}
			data-cy={`${dataCyPrefix}-continuous-${section.key}`}
		>
			<SectionComponent dataCy={dataCyPrefix} />
		</section>
	{/each}
</div>

<style>
	/*
		Each section leads with its own border-b divider. The first section sits
		directly under the sticky tab bar, whose own underline already draws that
		line — so its leading divider would double it. Hide only that one.
	*/
	.first-section > :global(div:first-child) {
		display: none;
	}
</style>
